export type SupportFaqInlinePart =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'link'; label: string; href: string };

export type SupportFaqAnswerBlock =
  | { type: 'paragraph'; parts: SupportFaqInlinePart[] }
  | { type: 'list-item'; parts: SupportFaqInlinePart[] };

export interface SupportFaqItem {
  id: string;
  number: number;
  question: string;
  answer: SupportFaqAnswerBlock[];
}

export interface SupportFaqSection {
  id: string;
  title: string;
  items: SupportFaqItem[];
}

const CADASTRO_EMAIL = 'cadastro@b3.com.br';
const CADASTRO_MAILTO = `mailto:${CADASTRO_EMAIL}`;
const PRODUTOS_ESG_EMAIL = 'produtosesg@b3.com.br';
const PRODUTOS_ESG_MAILTO = `mailto:${PRODUTOS_ESG_EMAIL}`;
const CARBON_PLATFORM_URL = 'https://creditocarbono.b3.com.br/';
const SAT_EMAIL = 'sat@b3.com.br';
const SAT_MAILTO = `mailto:${SAT_EMAIL}`;

const text = (value: string): SupportFaqInlinePart => ({ type: 'text', value });
const bold = (value: string): SupportFaqInlinePart => ({ type: 'bold', value });
const link = (label: string, href: string): SupportFaqInlinePart => ({ type: 'link', label, href });
const paragraph = (...parts: SupportFaqInlinePart[]): SupportFaqAnswerBlock => ({
  type: 'paragraph',
  parts,
});
const listItem = (...parts: SupportFaqInlinePart[]): SupportFaqAnswerBlock => ({
  type: 'list-item',
  parts,
});

export const SUPPORT_FAQ_SECTIONS: SupportFaqSection[] = [
  {
    id: 'platforms',
    title: 'Sobre as plataformas B3 e ACX',
    items: [
      {
        id: 'platforms-difference',
        number: 1,
        question: 'Qual a diferença entre a B3 e a ACX Brasil? Qual o papel de cada uma?',
        answer: [
          paragraph(
            text('A '),
            bold('B3'),
            text(' é responsável pelo '),
            bold('registro'),
            text(
              ' dos créditos de carbono em ambiente estruturado e regulado (Registro B3), gerando o instrumento financeiro (CARB). A ',
            ),
            bold('ACX Brasil'),
            text(' é a plataforma de '),
            bold('negociação'),
            text(' (spot e leilões, livro global de ordens) e responsável pela '),
            bold('custódia'),
            text(
              ' dos créditos diretamente nas certificadoras internacionais. Por isso, na maioria dos casos, o cliente precisa estar cadastrado nas duas plataformas — exceto para créditos CDM, tratados só na B3 (perfil Usuário B3), pois a ACX não possui conta de custódia na UNFCCC.',
            ),
          ),
        ],
      },
      {
        id: 'platforms-certifiers',
        number: 2,
        question:
          'Quais certificadoras (standards) a B3 aceita para registro de créditos de carbono?',
        answer: [
          paragraph(
            text(
              'Gold Standard, Verra e Clean Development Mechanism (CDM). Os dois primeiros seguem o fluxo integrado com a ACX; o CDM é registrado apenas pelo perfil "Usuário B3", em ambiente não integrado com a ACX Brasil.',
            ),
          ),
        ],
      },
      {
        id: 'platforms-profiles',
        number: 3,
        question: 'Quais são os tipos de conta e de perfil de usuário na plataforma?',
        answer: [
          listItem(
            bold('Conta Detentora (miolo 00):'),
            text(' identifica quem é o detentor/titular dos créditos de carbono.'),
          ),
          listItem(
            bold('Conta Cliente (miolo 10):'),
            text(
              ' identifica quem é o detentor/titular dos créditos de carbono por meio de seu comitente vinculado.',
            ),
          ),
          listItem(
            bold('Comitente:'),
            text(
              ' identifica quem é o detentor/titular (CPF/CNPJ) dos créditos de carbono vinculado à Conta Cliente.',
            ),
          ),
          listItem(
            bold('Conta Registradora/Emissora (miolo 40):'),
            text(' conta usada para lançar o registro do crédito (mesmo titular).'),
          ),
          listItem(
            bold('Perfil "Participante do Mercado":'),
            text(
              ' empresas com acesso às duas plataformas (B3 + ACX), fluxo padrão de registro e negociação.',
            ),
          ),
          listItem(
            bold('Perfil "Usuário B3":'),
            text(
              ' usuário administrador B3, usado apenas em ambiente não integrado à ACX — exclusivo para créditos certificados por CDM.',
            ),
          ),
          listItem(
            text('No NoMe: '),
            bold('Administrador'),
            text(' (gerencia usuários, não lança operações) x '),
            bold('Operador'),
            text(' (lança e confirma operações na plataforma de carbono).'),
          ),
        ],
      },
    ],
  },
  {
    id: 'access',
    title: 'Cadastro e acesso à plataforma',
    items: [
      {
        id: 'access-channels',
        number: 4,
        question:
          'Para quais canais envio a documentação de cadastro e tiro dúvidas sobre o produto?',
        answer: [
          listItem(
            bold('Cadastro/documentação societária'),
            text(
              ' (Ficha de Participante, outorga de acesso, RG/CPF, Contrato Social): Envie um e-mail para ',
            ),
            link(CADASTRO_EMAIL, CADASTRO_MAILTO),
            text('.'),
          ),
          listItem(
            bold('Dúvidas sobre a plataforma de registro da B3 e sobre o produto:'),
            text(' e-mail '),
            link(PRODUTOS_ESG_EMAIL, PRODUTOS_ESG_MAILTO),
            text('.'),
          ),
          paragraph(
            text('Recomenda-se copiar ambos os canais ao enviar documentos do operador.'),
          ),
        ],
      },
      {
        id: 'access-acx',
        number: 5,
        question: 'Preciso me cadastrar na ACX também? Como funciona?',
        answer: [
          paragraph(
            text(
              'Sim, para negociar. Clientes já cadastrados na B3 têm acesso simplificado: basta solicitar o formulário simplificado de onboarding pelo e-mail ',
            ),
            link(PRODUTOS_ESG_EMAIL, PRODUTOS_ESG_MAILTO),
            text(
              ', preencher os dados, indicar até dois administradores da conta e enviar o documento para a B3.',
            ),
          ),
        ],
      },
      {
        id: 'access-nome',
        number: 6,
        question:
          'Como faço o cadastro (set) do usuário no NoMe para acessar a plataforma de carbono?',
        answer: [
          paragraph(
            text('Após a conclusão do cadastro na B3, você recebe por e-mail (remetente '),
            link(CADASTRO_EMAIL, CADASTRO_MAILTO),
            text(') os números de conta e as credenciais de um usuário '),
            bold('administrador'),
            text(' no sistema NoMe. Esse administrador deve:'),
          ),
          listItem(
            text('Acessar Controle de Acesso > Usuário > Inclusão de Usuário;'),
          ),
          listItem(text('Cadastrar um usuário '), bold('operador'), text(';')),
          listItem(
            text('Enviar os dados do operador para '),
            link(PRODUTOS_ESG_EMAIL, PRODUTOS_ESG_MAILTO),
            text(' (mesmo canal de dúvidas sobre o produto);'),
          ),
          listItem(
            text('Aguardar o e-mail "Ping Identity" enviado ao '),
            bold('operador'),
            text(', com CPF e senha de acesso à plataforma de créditos de carbono.'),
          ),
          paragraph(
            bold('Importante:'),
            text(
              ' o administrador não lança registros — só o operador pode. Administrador e operador devem ser pessoas (CPFs) diferentes.',
            ),
          ),
        ],
      },
      {
        id: 'access-platform',
        number: 7,
        question: 'Como acesso a Plataforma de Créditos de Carbono da B3?',
        answer: [
          paragraph(
            text('Acesse '),
            link(CARBON_PLATFORM_URL, CARBON_PLATFORM_URL),
            text(
              ' e faça login com seu CPF e a senha fornecida pela B3 via Ping Identity (e-mail específico enviado após a configuração do usuário no NoMe). As credenciais não serão as mesmas do Sistema NoMe.',
            ),
          ),
        ],
      },
    ],
  },
  {
    id: 'support',
    title: 'Suporte',
    items: [
      {
        id: 'support-password',
        number: 8,
        question:
          'Minha senha expirou e não consigo acessar a plataforma de créditos de carbono — o que fazer?',
        answer: [
          paragraph(
            text(
              'A senha do NoMe expira automaticamente a cada 3 meses. Enquanto estiver expirada, não é possível fazer login na Plataforma de Créditos de Carbono (a autenticação via Ping Identity se conecta ao NoMe para liberar os permissionamentos). É necessário trocar a senha no NoMe antes de tentar acessar a plataforma novamente.',
            ),
          ),
          paragraph(
            text('Em caso de dificuldades contate o time de suporte avançado de tecnologia da B3 ('),
            link(SAT_EMAIL, SAT_MAILTO),
            text(').'),
          ),
        ],
      },
    ],
  },
];
